import { useState } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SegmentedControl,
} from '@/shared/components';
import type { CleaningTaskResponse } from '@/shared/api';
import { can, useAuth } from '@/shared/auth';
import { cn } from '@/shared/lib/cn';
import { PRIVILEGES } from '@stayon/auth-core';
import { ReportList } from './ReportList';
import { ReportDamageForm } from './ReportDamageForm';
import { ReportMissingForm } from './ReportMissingForm';
import { ReportSummary } from './ReportSummary';
import { useReportMutations } from './useReportMutations';

type Tab = 'damage' | 'missing';

const ACTION =
  'rounded-pill h-11 flex-1 border-[1.5px] bg-white px-3 text-[13px] font-bold disabled:opacity-40';

/**
 * Meldungen als Karte (Board B): offene Meldungen je eine Zeile, darunter „Schaden melden“ und
 * „Fehlteil melden“. Erfassen bleibt ein fokussierter Dialog mit vorgewähltem Reiter. `locked`
 * nennt den Grund, warum Melden gerade nicht geht (ohne Netz, vor dem Start).
 */
export function CleaningReportsSection({
  task,
  locked = null,
}: {
  task: CleaningTaskResponse;
  locked?: string | null;
}) {
  const { user } = useAuth();
  const canWork = can(user, PRIVILEGES.cleaningOwnTaskWork);
  const canAcknowledge = can(user, PRIVILEGES.cleaningAssignmentsManage);
  const m = useReportMutations(task.id);
  const [tab, setTab] = useState<Tab>('damage');
  const [open, setOpen] = useState(false);
  const damages = task.damages.filter((d) => d.kind === 'Damage');
  const missing = task.damages.filter((d) => d.kind === 'MissingItem');
  const show = (next: Tab) => {
    setTab(next);
    setOpen(true);
  };
  const listProps = {
    taskId: task.id,
    photos: task.photos,
    canWork,
    canAcknowledge,
    onAcknowledge: (reportId: string) => m.acknowledge.mutate(reportId),
    onCapture: (reportId: string, file: File) => m.attachPhoto.mutate({ reportId, file }),
    onRetake: (photoId: string) => m.removePhoto.mutate(photoId),
    attachingReportId: m.attachPhoto.isPending ? m.attachPhoto.variables?.reportId : undefined,
    retakingPhotoId: m.removePhoto.isPending ? m.removePhoto.variables : undefined,
  };

  return (
    <section
      aria-label="Meldungen"
      className="border-hairline rounded-tile flex flex-col gap-2 border bg-white p-3"
    >
      <ReportSummary reports={task.damages} />
      <div className="flex gap-2">
        {canWork ? (
          <>
            <button
              type="button"
              disabled={Boolean(locked)}
              onClick={() => show('damage')}
              className={cn(ACTION, 'border-sealwax text-sealwax')}
            >
              Schaden melden
            </button>
            <button
              type="button"
              disabled={Boolean(locked)}
              onClick={() => show('missing')}
              className={cn(ACTION, 'border-ink text-ink')}
            >
              Fehlteil melden
            </button>
          </>
        ) : (
          <button type="button" onClick={() => show('damage')} className={cn(ACTION, 'border-ink')}>
            Meldungen ansehen
          </button>
        )}
      </div>
      {canWork && locked && <p className="text-ink-soft text-xs">{locked}</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92dvh] overflow-hidden sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{canWork ? 'Meldung erfassen' : 'Meldungen'}</DialogTitle>
            <DialogDescription>
              {canWork
                ? 'Schaden oder Fehlteil festhalten; Fotos bleiben im Original.'
                : 'Gemeldete Schäden und Fehlteile.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="max-h-[calc(92dvh-7rem)] space-y-5 overflow-y-auto">
            <SegmentedControl
              value={tab}
              onChange={(value) => setTab(value as Tab)}
              options={[
                { value: 'damage', label: `Schäden (${damages.length})` },
                { value: 'missing', label: `Fehlteile (${missing.length})` },
              ]}
            />
            {tab === 'damage' && canWork && !locked && (
              <ReportDamageForm
                onReport={(body) => m.reportDamage.mutate(body)}
                isPending={m.reportDamage.isPending}
              />
            )}
            {tab === 'missing' && canWork && !locked && (
              <ReportMissingForm
                onReport={(body) => m.reportMissing.mutate(body)}
                isPending={m.reportMissing.isPending}
              />
            )}
            <ReportList
              {...listProps}
              reports={tab === 'damage' ? damages : missing}
              emptyLabel={tab === 'damage' ? 'Keine Schäden gemeldet.' : 'Kein Fehlteil gemeldet.'}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </section>
  );
}
